import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Star, MessageSquare } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

const formSchema = z.object({
  name: z.string().min(2, "El nombre debe tener al menos 2 caracteres"),
  company: z.string().min(2, "El nombre de la empresa debe tener al menos 2 caracteres"),
  message: z.string().min(10, "La reseña debe tener al menos 10 caracteres")
});

type FormValues = z.infer<typeof formSchema>;

interface ReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ReviewModal({ isOpen, onClose }: ReviewModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedRating, setSelectedRating] = useState(0);
  const { toast } = useToast();
  
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      company: "",
      message: ""
    }
  });

  const onSubmit = async (values: FormValues) => {
    console.log("Form submitted with values:", values);
    console.log("Selected rating:", selectedRating);
    
    if (selectedRating === 0) {
      console.log("Rating validation failed");
      toast({
        title: "Error",
        description: "Por favor selecciona una calificación",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);
    console.log("Starting submission...");
    
    try {
      const reviewData = {
        name: values.name,
        company: values.company,
        message: values.message,
        rating: selectedRating
      };
      
      console.log("Sending to database:", reviewData);
      
      const { data, error } = await supabase
        .from('reviews')
        .insert(reviewData)
        .select();

      console.log("Database response:", { data, error });

      if (error) {
        throw error;
      }

      console.log("Review saved successfully");
      
      toast({
        title: "¡Gracias por tu reseña!",
        description: "Tu opinión es muy valiosa para nosotros y otros empresarios.",
        variant: "default"
      });

      form.reset();
      setSelectedRating(0);
      onClose();
    } catch (error) {
      console.error('Error submitting review:', error);
      toast({
        title: "Error",
        description: "Hubo un problema al enviar tu reseña. Por favor intenta de nuevo.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStars = () => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`h-8 w-8 cursor-pointer transition-colors ${
          i < selectedRating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300 hover:text-yellow-400'
        }`}
        onClick={() => setSelectedRating(i + 1)}
      />
    ));
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-primary" />
            Comparte tu experiencia
          </DialogTitle>
          <DialogDescription>
            Tu opinión nos ayuda a mejorar y ayuda a otros empresarios a tomar la mejor decisión.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nombre completo</FormLabel>
                  <FormControl>
                    <Input placeholder="Tu nombre" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="company"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Empresa</FormLabel>
                  <FormControl>
                    <Input placeholder="Nombre de tu empresa" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="space-y-2">
              <FormLabel>Calificación</FormLabel>
              <div className="flex gap-1">
                {renderStars()}
              </div>
              {selectedRating > 0 && (
                <p className="text-sm text-muted-foreground">
                  {selectedRating === 1 && "Muy insatisfecho"}
                  {selectedRating === 2 && "Insatisfecho"}
                  {selectedRating === 3 && "Neutral"}
                  {selectedRating === 4 && "Satisfecho"}
                  {selectedRating === 5 && "Muy satisfecho"}
                </p>
              )}
            </div>

            <FormField
              control={form.control}
              name="message"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tu reseña</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="Cuéntanos sobre tu experiencia con MKTN Guardians..." 
                      className="min-h-[100px]"
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button 
                type="submit" 
                disabled={isSubmitting || selectedRating === 0}
                className="flex-1"
              >
                {isSubmitting ? "Enviando..." : "Enviar reseña"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}